import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, CreditCard, ShoppingCart, HelpCircle, Phone, MapPin, ArrowRight, FileText, Users, Heart } from 'lucide-react';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { TrustBadge } from '../components/ui/TrustBadge';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const wicEligibleItems = ['Fresh fruits and vegetables', 'Milk and dairy products', 'Whole grain cereals and bread', 'Eggs', 'Peanut butter', 'Beans and legumes', 'Infant formula and baby food', '100% fruit juice'];
const ebtEligibleItems = ['All fresh produce', 'Meat, poultry, and fish', 'Dairy products', 'Breads and cereals', 'Snack foods', 'Non-alcoholic beverages', 'Seeds and plants for food', 'Most packaged foods'];
const faqs = [{
  question: 'How do I use my WIC benefits at Diamond International Mart?',
  answer: 'Simply present your WIC card or eWIC card at checkout. Our staff will help you identify WIC-eligible items and process your benefits. Look for the green WIC badge on eligible products throughout the store.'
}, {
  question: 'Can I use EBT for delivery orders?',
  answer: 'Yes! We accept EBT/SNAP benefits for both in-store purchases and delivery orders through our partner platforms. Simply select EBT as your payment method at checkout.'
}, {
  question: 'What if an item is out of stock?',
  answer: 'If a WIC-eligible item is out of stock, we can substitute it with an equivalent approved item. Our staff is trained to help you find suitable alternatives that meet WIC requirements.'
}, {
  question: 'Do you accept both WIC and EBT in the same transaction?',
  answer: 'Absolutely! You can use both WIC and EBT benefits in a single shopping trip. WIC items will be processed first, then remaining eligible items can be paid with EBT.'
}];
export function WICEBTGuidePage() {
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-400">
      <TopNav data-id="element-401" />

      <main className="pb-24 md:pb-8" data-id="element-402">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#047857] to-[#059669] text-white py-12 md:py-20" data-id="element-403">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center" data-id="element-404">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-id="element-405">
              <CheckCircle2 className="w-5 h-5" data-id="element-406" />
              <span className="font-semibold" data-id="element-407">
                Authorized WIC & EBT Retailer
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-408">
              Using WIC & EBT at Diamond International Mart
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto" data-id="element-409">
              We're proud to accept WIC and EBT/SNAP benefits. Find everything
              you need for your family's traditional recipes while using your
              benefits.
            </motion.p>
          </div>
        </section>

        {/* Quick Info Cards */}
        <section className="py-12 md:py-16 px-4 md:px-6 max-w-6xl mx-auto -mt-8" data-id="element-410">
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-411">
            {[{
            icon: CreditCard,
            title: 'Easy Payment',
            description: 'Use your WIC or EBT card just like any other payment method'
          }, {
            icon: ShoppingCart,
            title: 'Wide Selection',
            description: 'Hundreds of eligible items including African & Caribbean specialties'
          }, {
            icon: Users,
            title: 'Helpful Staff',
            description: 'Our team is trained to assist with benefit questions'
          }].map(item => <motion.div key={item.title} variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100" data-id="element-412">
                <div className="w-12 h-12 bg-[#10b981]/10 rounded-xl flex items-center justify-center mb-4" data-id="element-413">
                  <item.icon className="w-6 h-6 text-[#10b981]" data-id="element-414" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-415">{item.title}</h3>
                <p className="text-gray-600 text-sm" data-id="element-416">{item.description}</p>
              </motion.div>)}
          </motion.div>
        </section>

        {/* Eligible Items */}
        <section className="py-12 md:py-16 bg-white" data-id="element-417">
          <div className="max-w-6xl mx-auto px-4 md:px-6" data-id="element-418">
            <div className="text-center mb-12" data-id="element-419">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-420">
                What Can You Buy?
              </h2>
              <p className="text-gray-600" data-id="element-421">
                Here's what's covered by WIC and EBT benefits
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-422">
              {/* WIC */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-gradient-to-br from-[#047857]/5 to-[#047857]/10 rounded-2xl p-6 md:p-8" data-id="element-423">
                <div className="flex items-center gap-3 mb-6" data-id="element-424">
                  <TrustBadge type="WIC" data-id="element-425" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-426">
                    WIC Eligible Items
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-427">
                  {wicEligibleItems.map(item => <li key={item} className="flex items-center gap-3" data-id="element-428">
                      <CheckCircle2 className="w-5 h-5 text-[#047857] flex-shrink-0" data-id="element-429" />
                      <span className="text-gray-700" data-id="element-430">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              {/* EBT */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-gradient-to-br from-[#1E3A5F]/5 to-[#1E3A5F]/10 rounded-2xl p-6 md:p-8" data-id="element-431">
                <div className="flex items-center gap-3 mb-6" data-id="element-432">
                  <TrustBadge type="EBT" data-id="element-433" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-434">
                    EBT/SNAP Eligible Items
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-435">
                  {ebtEligibleItems.map(item => <li key={item} className="flex items-center gap-3" data-id="element-436">
                      <CheckCircle2 className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" data-id="element-437" />
                      <span className="text-gray-700" data-id="element-438">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 md:py-16 px-4 md:px-6 max-w-6xl mx-auto" data-id="element-439">
          <div className="text-center mb-12" data-id="element-440">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-441">
              How to Shop With Benefits
            </h2>
            <p className="text-gray-600" data-id="element-442">Simple steps to use your WIC or EBT</p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-4 gap-6" data-id="element-443">
            {[{
            step: '1',
            title: 'Shop',
            description: 'Browse our selection and look for WIC/EBT badges on eligible items'
          }, {
            step: '2',
            title: 'Check',
            description: 'Review your cart to ensure items are covered by your benefits'
          }, {
            step: '3',
            title: 'Pay',
            description: 'Present your WIC or EBT card at checkout'
          }, {
            step: '4',
            title: 'Enjoy',
            description: 'Take home fresh, authentic groceries for your family'
          }].map((item, index) => <motion.div key={item.step} variants={itemVariants} className="text-center" data-id="element-444">
                <div className="w-14 h-14 bg-[#D97757] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-445">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-446">{item.title}</h3>
                <p className="text-sm text-gray-600" data-id="element-447">{item.description}</p>
                {index < 3 && <ArrowRight className="w-6 h-6 text-gray-300 mx-auto mt-4 hidden md:block" data-id="element-448" />}
              </motion.div>)}
          </motion.div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16 bg-white" data-id="element-449">
          <div className="max-w-4xl mx-auto px-4 md:px-6" data-id="element-450">
            <div className="text-center mb-12" data-id="element-451">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-452">
                <HelpCircle className="w-6 h-6 text-[#10b981]" data-id="element-453" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-454">
                  Frequently Asked Questions
                </h2>
              </div>
            </div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-4" data-id="element-455">
              {faqs.map(faq => <motion.div key={faq.question} variants={itemVariants} className="bg-gray-50 rounded-2xl p-6" data-id="element-456">
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-457">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600" data-id="element-458">{faq.answer}</p>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-12 md:py-16 px-4 md:px-6" data-id="element-459">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#1E3A5F] to-[#2D5F3F] rounded-3xl p-8 md:p-12 text-white" data-id="element-460">
            <div className="text-center mb-8" data-id="element-461">
              <Heart className="w-12 h-12 mx-auto mb-4 text-[#F4A261]" data-id="element-462" />
              <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-463">
                Need Help?
              </h2>
              <p className="text-white/80" data-id="element-464">
                Our friendly staff is here to assist you with any questions
                about using your benefits.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-465">
              <a href="tel:2406079834" className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors" data-id="element-466">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center" data-id="element-467">
                  <Phone className="w-6 h-6" data-id="element-468" />
                </div>
                <div data-id="element-469">
                  <div className="text-sm text-white/70" data-id="element-470">Call us</div>
                  <div className="font-bold" data-id="element-471">(240) 607-9834</div>
                </div>
              </a>
              <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4" data-id="element-472">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center" data-id="element-473">
                  <MapPin className="w-6 h-6" data-id="element-474" />
                </div>
                <div data-id="element-475">
                  <div className="text-sm text-white/70" data-id="element-476">Visit us</div>
                  <div className="font-bold" data-id="element-477">
                    3651 Leonardtown Rd, Waldorf, MD
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center" data-id="element-478">
              <Link to="/search" className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] px-8 py-4 rounded-xl font-bold hover:bg-white/90 transition-colors" data-id="element-479">
                Start Shopping <ArrowRight className="w-5 h-5" data-id="element-480" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <BottomNav data-id="element-481" />
    </div>;
}